
///
/// EarthBrowser
///
/// Created by Matt Giger on 8/16/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { time, timedelta, timerange } from "@stovokor/math/time"

export interface DatasetFrame {
	url: string
	range: timerange
	loading: boolean
	loaded: boolean
	load(): Promise<void>
	purge(): void
}

export class DatasetSeries<T extends DatasetFrame> {
	public currentFrame?: T
	public nextFrame?: T
	public range = new timerange(time.longAgo, time.longAgo)
	public endOffset = timedelta.hours(0)
	public frequency = timedelta.hours(1)
	public fadeoutDuration = timedelta.minutes(0) // Duration to fade out after end of data (0 = no fadeout)
	public availabilityFactor = 1.0 // 1.0 = fully visible, 0.0 = fully faded

	private frames: T[] = []
	// private preloadCount: number = 5
	/** Purge loaded frame data outside the current/next window on update.
	 *  Only safe for frames that can re-load their data (e.g. from network);
	 *  leave false for frames whose data cannot be recovered after purge. */
	public purgeUnused: boolean = false
	private lastUpdateTime: number = 0
	// performance.now() when currentFrame last changed, for the load state's wait timer
	private currentSince: number = 0

	/**
	 * The clock points at a frame whose data hasn't loaded yet, so the
	 * layer is drawing stale or no data for this time. Null once loaded
	 */
	public loadState(): LayerLoadState | null {
		const current = this.currentFrame
		if(!current || current.loaded) {
			return null
		}
		return {
			phase: "frames",
			atMs: current.range.start.date.getTime(),
			since: this.currentSince,
		}
	}

	// Set new frames while preserving existing and overlapping frames (determined by URL uniqueness)
	public setFrames(frames: T[]): void {
		this.currentFrame = undefined
		this.nextFrame = undefined

		if(this.purgeUnused) {
			const purgedFrames = this.frames.filter((frame) => { return !DatasetSeries.hasFrame(frames, frame.url) })
			purgedFrames.forEach(frame => frame.purge())
		}

		const newFrames = frames.filter((frame) => { return !DatasetSeries.hasFrame(this.frames, frame.url) })
		const overlapFrames = this.frames.filter((frame) => { return DatasetSeries.hasFrame(frames, frame.url) })
		this.frames = overlapFrames.concat(newFrames).sort((a, b) => { return a.range.start.julian - b.range.start.julian })
		const first = this.frames[0]
		const last = this.frames[this.frames.length-1]
		if(first && last) {
			const newRange = new timerange(first.range.start, last.range.end.adding(this.endOffset))
			this.range = newRange
		} else {
			this.range = new timerange()
		}
	}

	/**
	 * Drop every frame's loaded data but keep the frame list, so a hidden
	 * layer releases its memory yet can re-load when shown again. Resetting
	 * currentFrame/lastUpdateTime forces the next update() to re-select the
	 * current frame and trigger fresh loads. Only safe when frames can
	 * re-load their data (same constraint as purgeUnused).
	 */
	public purgeAll(): void {
		this.frames.forEach(frame => frame.purge())
		this.currentFrame = undefined
		this.nextFrame = undefined
		this.lastUpdateTime = 0
	}

	/** Time span covered by the current frame list (undefined when empty) */
	public get timeRange(): timerange | undefined {
		return this.frames.length > 0 ? this.range : undefined
	}

	/** Start time (ms) of every frame, ascending. Feeds the timeline's frame stepping */
	public frameTimesMs(): number[] {
		return this.frames.map(f => f.range.start.date.getTime())
	}

	/** Time spans whose frame data is loaded, merging runs of consecutive loaded frames */
	public loadedRangesMs(): Array<{ startMs: number; endMs: number }> {
		const out: Array<{ startMs: number; endMs: number }> = []
		const count = this.frames.length
		for(let i = 0; i < count; ++i) {
			const frame = this.frames[i]
			if(!frame.loaded) {
				continue
			}
			const startMs = frame.range.start.date.getTime()
			const next = this.frames[i + 1]
			const endMs = next ? next.range.start.date.getTime() : frame.range.end.adding(this.endOffset).date.getTime()
			const last = out[out.length - 1]
			if(last && last.endMs >= startMs) {
				last.endMs = Math.max(last.endMs, endMs)
			} else {
				out.push({ startMs, endMs })
			}
		}
		return out
	}

	private static hasFrame(array: DatasetFrame[], url: string): boolean {
		for(const frame of array) {
			if(frame.url == url) {
				return true
			}
		}
		return false
	}

	public update(time: time): void {
		if(this.frames.length == 0) {
			this.availabilityFactor = 0.0
			return
		}

		// Save processing power by not updating if the frames are unlikely to have changed
		if(Math.abs(time.julian - this.lastUpdateTime) < this.frequency.jdelta / 2) {
			return
		}
		this.lastUpdateTime = time.julian

		if(this.range.contains(time))  {
			this.availabilityFactor = 1.0
			if(!this.currentFrame?.range.contains(time)) {
				const frame = this.frames.find((frame) => { return frame.range.contains(time) })
				if(frame) {
					// console.log(`Updating frame ${frame.range.start.date.toISOString()}`)
					this.currentFrame = frame
					this.currentSince = performance.now()
					if(!frame.loaded && !frame.loading) {
						void frame.load()
					}

					this.nextFrame = this.frames[this.frames.indexOf(frame)+1]
					if(this.nextFrame && !this.nextFrame.loaded && !this.nextFrame.loading) {
						void this.nextFrame.load()
					}
				}
			}
		} else if(this.fadeoutDuration.jdelta > 0 && time.julian > this.range.end.julian) {
			// Calculate fadeout factor when past end of data
			const timePastEnd = time.julian - this.range.end.julian
			const fadeoutJulian = this.fadeoutDuration.jdelta
			this.availabilityFactor = Math.max(0.0, 1.0 - (timePastEnd / fadeoutJulian))
		} else {
			this.availabilityFactor = 0.0
		}

		// Keep memory bounded while scrubbing: drop loaded data outside the
		// current/next window (frames re-load on demand when they return)
		if(this.purgeUnused) {
			for(const frame of this.frames) {
				if(frame !== this.currentFrame && frame !== this.nextFrame && frame.loaded && !frame.loading) {
					frame.purge()
				}
			}
		}
	}
}
